import { cn } from '@/lib/utils';
import { forwardRef, type HTMLAttributes } from 'react';

export interface PageLoaderProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  showBrowserFrame?: boolean;
}

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="w-8 h-8 relative">
      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
    </div>
  );
}

// Browser frame component (optional)
function BrowserFrame() {
  return (
    <div className="absolute top-0 left-0 right-0 bg-dark-800 h-[50px] w-full z-10">
      {/* Browser buttons */}
      <div className="absolute left-8 top-1/2 transform -translate-y-1/2 flex gap-2">
        <div className="w-3 h-3 bg-error-600 rounded-full" />
        <div className="w-3 h-3 bg-warning-500 rounded-full" />
        <div className="w-3 h-3 bg-success-500 rounded-full" />
      </div>

      {/* Browser bar */}
      <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-dark-900 rounded-xl px-4 py-1 min-w-[300px] md:min-w-[592px]">
        <p className="text-dark-100 text-xs text-center whitespace-nowrap">
          support.mypaynow.com.au
        </p>
      </div>
    </div>
  );
}

export const PageLoader = forwardRef<HTMLDivElement, PageLoaderProps>(
  ({ className, showBrowserFrame = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('fixed inset-0 bg-primary-600 z-50', className)}
        {...props}
      >
        {/* Browser frame (optional) */}
        {showBrowserFrame && <BrowserFrame />}

        {/* Background Ellipses - same as HomePage */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Ellipse 3 - Bottom Left Yellow Gradient */}
          <div className="absolute -bottom-64 -left-32 w-[600px] h-[600px] rounded-full bg-gradient-radial from-[rgba(245,234,97,0.4)] to-transparent to-65% blur-[80px]" />

          {/* Ellipse 2 - Top Right Purple/Pink Gradient */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-radial from-[rgba(236,64,255,0.35)] to-transparent to-65% blur-[80px]" />
        </div>

        {/* Content */}
        <div
          className={cn(
            'relative z-10 flex flex-col items-center justify-center min-h-screen',
            showBrowserFrame && 'pt-12'
          )}
        >
          {/* Loading Spinner */}
          <LoadingSpinner />
        </div>
      </div>
    );
  }
);

PageLoader.displayName = 'PageLoader';
